import React, { useEffect, useState } from 'react';
import './style.less';
import { SpotlightToggleCard } from '../ToggleCard';
import { SpotlightAccount, SpotlightUTM, SpotlightUTMRow } from 'types/spotlight';
import { Icon } from 'shared/components/Icon';
import { getSpotlightAccountUTM, getSpotlightContactUTM } from 'shared/api';
import { Popover } from 'antd';
import clsx from 'clsx';
import { Button } from 'shared/components/Button';
import Skeleton from 'react-loading-skeleton';
import { SpotlightUtmsDetails } from './Details';
import { toCamelCase } from 'shared/utils/formatters';
import { LBBContact } from 'sb-lib/types';

export const SpotlightUtms: React.FC<{
  account?: SpotlightAccount | null;
  contact?: LBBContact | null;
}> = ({ account, contact }) => {
  const [loading, setLoading] = useState(true);
  const [utms, setUtms] = useState<SpotlightUTMRow[]>([]);
  const [by, setBy] = useState<SpotlightUTM>('utm_source');
  const [selectOpen, setSelectOpen] = useState(false);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const labels: Record<SpotlightUTM, string> = {
    utm_source: 'Source',
    utm_medium: 'Medium',
    utm_campaign: 'Campaign',
    utm_content: 'Content',
    utm_term: 'Term',
  };

  useEffect(() => {
    if (!account?.id && !contact?.id) return;
    setLoading(true);

    const id = contact?.id ?? account?.id;

    const fn = contact?.id ? getSpotlightContactUTM : getSpotlightAccountUTM;

    /* @ts-ignore */
    fn(id, by, 1).then(({ data }) => {
      setUtms(data.results);
      setSessionTotal(data.results.reduce((acc, u) => acc + u.sessions, 0));
      setLoading(false);
    });
  }, [by, account?.id, contact?.id]);

  return (
    <>
      <SpotlightToggleCard
        title={
          <>
            <Icon icon="linkSquare" /> UTMs
          </>
        }
      >
        <div className="SpotlightUtms">
          <div className="SpotlightUtms__header">
            <Popover
              trigger="click"
              overlayClassName="SpotlightUtms__popover"
              onVisibleChange={o => setSelectOpen(o)}
              visible={selectOpen}
              destroyTooltipOnHide
              placement="bottomLeft"
              content={
                <div className="SpotlightUtms__options">
                  {Object.keys(labels).map(u => (
                    <div
                      className={clsx(
                        'SpotlightUtms__option',
                        by === u && 'SpotlightUtms__option--selected',
                      )}
                      key={u}
                      onClick={() => {
                        setBy(u as SpotlightUTM);
                        setSelectOpen(false);
                      }}
                    >
                      By {labels[u as SpotlightUTM]}
                    </div>
                  ))}
                </div>
              }
            >
              <button className="SpotlightUtms__trigger">
                UTMs by {labels[by]}
                <Icon icon="chevron" width={14} height={14} />
              </button>
            </Popover>
            <span>Sessions ({sessionTotal})</span>
          </div>
          <div className="SpotlightUtms__list">
            {(loading ? [...new Array(6)] : utms).map((u, i) => (
              <div key={i} className="SpotlightUtms__row">
                <div className="SpotlightUtms__cell">
                  {loading ? (
                    <Skeleton baseColor="#F6F7F9" width={44} height={11} />
                  ) : (
                    <>{u[toCamelCase(by)] ?? 'n/a'}</>
                  )}
                </div>
                <div className="SpotlightUtms__cellBar">
                  <div className="SpotlightUtms__bar">
                    <div
                      className="SpotlightUtms__barInner"
                      style={{
                        width: `${((u?.sessions ?? 0) / sessionTotal) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="SpotlightUtms__cell">
                  {loading ? (
                    <Skeleton baseColor="#F6F7F9" width={16} height={11} />
                  ) : (
                    <>{u?.sessions}</>
                  )}
                </div>
              </div>
            ))}
            {!utms.length && !loading && (
              <div className="SpotlightUtms__row SpotlightUtms__row--empty">
                <div className="SpotlightUtms__cell">No UTMs yet</div>
              </div>
            )}
          </div>
          {!!utms.length && (
            <Button
              className="SpotlightUtms__button"
              onClick={() => {
                setDetailsOpen(true);
              }}
              type="ghost"
            >
              See Details
              <Icon icon="arrowLongLeft" width={16} height={16} />
            </Button>
          )}
        </div>
      </SpotlightToggleCard>
      {(account?.id || contact?.id) && (
        <SpotlightUtmsDetails
          accountId={account?.id}
          contactId={contact?.id}
          visible={detailsOpen}
          setVisible={setDetailsOpen}
        />
      )}
    </>
  );
};
